import {securedApiRequest} from "./SecuredApi";


/**
 * Fetches notifications for a specific user based on their email address.
 *
 * This function encodes the provided email to ensure it can safely be included in a URL,
 * and then sends an HTTP GET request to retrieve notifications associated with the email.
 *
 * @param {string} email - The email address of the user for whom notifications are retrieved.
 * @returns {Promise<Object>} A Promise that resolves to the response containing the notifications data.
 */
export const fetchNotifications = async (email) => {
    const encodedEmail = encodeURIComponent(email);
    return securedApiRequest(`/notifications/?email=${encodedEmail}`, "GET");
};

/**
 * Initiates the payment process by sending a POST request to the server with the provided form data.
 *
 * This function sends the form data to the server's `/api/payment/initiate/` endpoint to start
 * the payment process. The request includes user credentials and is expected to return a JSON response.
 * If the server responds with a non-OK status, an error is thrown containing the server's error message or a default error message.
 *
 * @param {FormData} formData - The form data object containing the necessary payment details to initiate the transaction.
 * @returns {Promise<Object>} A promise that resolves to the JSON response from the server upon a successful request.
 * @throws {Error} If the server response is not OK or if any network or processing error occurs.
 */
export const initiatePayment = async (formData) => {
    try {
        const response = await fetch(`/api/payment/initiate/`, {
            method: "POST",
            credentials: "include",
            body: formData,
        });

        // Check if the response is OK
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "An error occurred, please try again.");
        }

        return await response.json();
    } catch (error) {
        console.error("Error in initiatePaymentWithFormData:", error.message);
        throw error;
    }
};

/**
 * Fetch PayFast signature and merchant details for a trip confirmation.
 * @param {Object} params
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} params.email
 * @param {string} params.reference
 * @param {number} params.amount
 */
export const fetchConfirmTripSignature = async ({
        firstName,
        lastName,
        email,
        reference,
        amount,
    }) => {
    const payload = {
        name_first: firstName,
        name_last: lastName,
        email_address: email,
        m_payment_id: reference,
        amount,
        item_name: reference,
    };
    return securedApiRequest("/confirm-trip/", "POST", payload);
};

/**
 * Fetch the current status and details of a trip by its reference.
 * Polling clients can re-call this to get updates.
 * @param {string} tripReference
 * @returns {Promise<object>} trip details JSON
 */
export const fetchTripStatus = async (tripReference) => {
    return securedApiRequest(
        `/get-trip-details/${encodeURIComponent(tripReference)}/`,
        "GET"
    );
};
